import React from 'react';
import { 
  Calendar, Plus, Printer, ArrowDownLeft, X, Loader2 
} from 'lucide-react';
import { updateDoc, doc, serverTimestamp } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';

interface NormalizedBudgetVarianceReport {
  budget: any | null;
  actuals: Record<string, number>;
  budgetValues: Record<string, number>;
  variances: Record<string, number>;
  totals: {
    actual: number;
    budget: number;
    variance: number;
  };
}

const toAmount = (value: any): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : undefined;
  }
  return undefined;
};

const normalizeBudgetValues = (source: any): Record<string, number> => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

  return Object.entries(source).reduce<Record<string, number>>((values, [key, value]) => {
    const amount = toAmount(value);
    if (amount !== undefined) values[key] = amount;
    return values;
  }, {});
};

const normalizeBudget = (budget: any): any => {
  const source = budget || {};
  const normalized = { ...source };
  const id = source.id ?? source.budgetId ?? source.budget_id ?? source._id;
  const name = source.name ?? source.budgetName ?? source.budget_name;
  const startDate = source.startDate ?? source.start_date;
  const period = source.period ?? source.periodMonths ?? source.period_months;
  const trackingSelections = source.trackingSelections ?? source.tracking_selections;
  const values = source.values ?? source.budgetValues ?? source.budget_values;

  if (id !== undefined) normalized.id = String(id);
  if (name !== undefined) normalized.name = name;
  if (startDate !== undefined) normalized.startDate = startDate;
  if (period !== undefined) normalized.period = Number(period);
  if (trackingSelections !== undefined) normalized.trackingSelections = trackingSelections;
  if (values !== undefined) normalized.values = normalizeBudgetValues(values);

  return normalized;
};

const getBudgetList = (payload: any): any[] => {
  const candidates = [
    payload,
    payload?.budgets,
    payload?.data,
    payload?.data?.budgets,
    payload?.items,
  ];

  return (candidates.find(Array.isArray) || []).map(normalizeBudget);
};

const normalizeAmountMap = (source: any, fields: string[]): Record<string, number> => {
  const normalized: Record<string, number> = {};
  const collection = source && typeof source === 'object' && !Array.isArray(source)
    ? source.accounts ?? source.items ?? source.lines ?? source.values ?? source
    : source;

  const addEntry = (fallbackKey: string, entry: any) => {
    const key = entry && typeof entry === 'object'
      ? entry.accountCode ?? entry.code ?? entry.account?.code ?? entry.accountId ?? entry.account?.id ?? entry.accountName ?? entry.account?.name ?? fallbackKey
      : fallbackKey;
    const rawAmount = entry && typeof entry === 'object'
      ? fields.map(field => entry[field]).find(value => toAmount(value) !== undefined)
      : entry;
    const amount = toAmount(rawAmount);

    if (key !== undefined && key !== null && amount !== undefined) {
      const accountKey = String(key);
      normalized[accountKey] = (normalized[accountKey] || 0) + amount;
    }
  };

  if (Array.isArray(collection)) {
    collection.forEach(entry => addEntry('', entry));
  } else if (collection && typeof collection === 'object') {
    Object.entries(collection).forEach(([key, entry]) => addEntry(key, entry));
  }

  return normalized;
};

const reportTotal = (report: any, fields: string[], fallback: number): number => {
  const sections = [report, report?.summary, report?.totals, report?.total];

  for (const section of sections) {
    if (!section || typeof section !== 'object') continue;
    for (const field of fields) {
      const amount = toAmount(section[field]);
      if (amount !== undefined) return amount;
    }
  }

  return fallback;
};

const normalizeBudgetVarianceReport = (payload: any): NormalizedBudgetVarianceReport => {
  const report = payload?.data ?? payload ?? {};
  const rows = report.rows ?? report.accounts ?? report.lineItems ?? report.items;
  const withRowsFallback = (source: any, fields: string[]) => {
    const values = normalizeAmountMap(source, fields);
    return Object.keys(values).length > 0 || !rows ? values : normalizeAmountMap(rows, fields);
  };
  const actuals = withRowsFallback(report.actuals ?? report.actual, ['actual', 'actuals', 'actualAmount', 'amount', 'value', 'total']);
  const budgetValues = withRowsFallback(report.budgetValues ?? report.budget?.values ?? report.budget, ['budget', 'budgeted', 'budgetAmount', 'amount', 'value', 'total']);
  const variances = withRowsFallback(report.variance ?? report.variances, ['variance', 'varianceAmount', 'amount', 'value', 'total']);
  const actualTotal = Object.values(actuals).reduce((sum, amount) => sum + amount, 0);
  const budgetTotal = Object.values(budgetValues).reduce((sum, amount) => sum + amount, 0);
  const varianceTotal = Object.values(variances).reduce((sum, amount) => sum + amount, 0);

  return {
    budget: report.budgetDetails ?? report.budget?.details ?? (Array.isArray(report.budget) ? null : report.budget) ?? null,
    actuals,
    budgetValues,
    variances,
    totals: {
      actual: reportTotal(report, ['totalActual', 'total_actual', 'actualTotal', 'actual_total', 'actual', 'actuals'], actualTotal),
      budget: reportTotal(report, ['totalBudget', 'total_budget', 'budgetTotal', 'budget_total', 'budget'], budgetTotal),
      variance: reportTotal(report, ['totalVariance', 'total_variance', 'varianceTotal', 'variance_total', 'variance'], varianceTotal),
    },
  };
};

const readResponseBody = async (response: Response): Promise<any | null> => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

interface BudgetSummaryProps {
  accounts: any[];
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  budgetPeriod: number;
  setBudgetPeriod: (period: number) => void;
  selectedBudgetId: string | null;
  setSelectedBudgetId: (id: string) => void;
  budgets: any[];
  setIsBudgetModalOpen: (val: boolean) => void;
  isBudgetModalOpen: boolean;
  trackingCategories: any[];
  activeBudget: any;
  budgetValues: Record<string, number>;
  newBudgetName: string;
  setNewBudgetName: (name: string) => void;
  newBudgetTrackingSelections: Record<string, string>;
  setNewBudgetTrackingSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleCreateBudget: () => void;
  isSavingBudget: boolean;
  setActiveReport: (report: string | null) => void;
}

export const BudgetSummary = ({
  accounts, startDate, setStartDate, endDate, setEndDate,
  budgetPeriod, setBudgetPeriod, selectedBudgetId, setSelectedBudgetId,
  budgets, setIsBudgetModalOpen, isBudgetModalOpen, trackingCategories,
  activeBudget, budgetValues, newBudgetName, setNewBudgetName,
  newBudgetTrackingSelections, setNewBudgetTrackingSelections,
  handleCreateBudget, isSavingBudget, setActiveReport
}: BudgetSummaryProps) => {
  const [localBudgets, setLocalBudgets] = React.useState<any[] | null>(null);
  const [localBudget, setLocalBudget] = React.useState<any | null>(null);
  const [localReport, setLocalReport] = React.useState<NormalizedBudgetVarianceReport | null>(null);
  const appAuthToken = typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');
  const localOrgId = typeof window === 'undefined'
    ? null
    : window.localStorage.getItem('selected_org_id')
      || window.localStorage.getItem('orgId')
      || window.localStorage.getItem('organizationId');
  const localBudgetsUrl = appAuthToken && localOrgId
    ? `/api/organizations/${encodeURIComponent(localOrgId)}/budgets`
    : null;

  React.useEffect(() => {
    if (!localBudgetsUrl || !appAuthToken || !selectedBudgetId) {
      setLocalBudgets(null);
      setLocalBudget(null);
      return;
    }

    const controller = new AbortController();
    const loadSelectedBudget = async () => {
      try {
        const response = await fetch(localBudgetsUrl, {
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            Accept: 'application/json',
          },
          signal: controller.signal,
        });
        const payload = await readResponseBody(response);
        if (!response.ok) throw new Error(payload?.detail || `Budget request failed: ${response.status}`);

        const loadedBudgets = getBudgetList(payload);
        const selectedBudget = loadedBudgets.find(budget => budget.id === selectedBudgetId) || null;
        if (controller.signal.aborted) return;

        setLocalBudgets(loadedBudgets);
        setLocalBudget(selectedBudget);
        if (selectedBudget?.period !== undefined && Number.isFinite(selectedBudget.period)) {
          setBudgetPeriod(selectedBudget.period);
        }
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') {
          // Retain the Firebase-backed props when the local API cannot provide this budget.
          setLocalBudgets(null);
          setLocalBudget(null);
        }
      }
    };

    void loadSelectedBudget();
    return () => controller.abort();
  }, [appAuthToken, localBudgetsUrl, selectedBudgetId, setBudgetPeriod]);

  React.useEffect(() => {
    if (!localBudgetsUrl || !appAuthToken || !selectedBudgetId) {
      setLocalReport(null);
      return;
    }

    const controller = new AbortController();
    const loadBudgetVariance = async () => {
      try {
        const response = await fetch(
          `/api/organizations/${encodeURIComponent(localOrgId!)}/reports/budget-variance?budgetId=${encodeURIComponent(selectedBudgetId)}`,
          {
            headers: {
              Authorization: `Bearer ${appAuthToken}`,
              Accept: 'application/json',
            },
            signal: controller.signal,
          },
        );
        const payload = await readResponseBody(response);
        if (!response.ok) throw new Error(payload?.detail || `Budget variance request failed: ${response.status}`);
        if (!controller.signal.aborted) setLocalReport(normalizeBudgetVarianceReport(payload));
      } catch (error) {
        if ((error as { name?: string }).name !== 'AbortError') setLocalReport(null);
      }
    };

    setLocalReport(null);
    void loadBudgetVariance();
    return () => controller.abort();
  }, [appAuthToken, localBudgetsUrl, localOrgId, selectedBudgetId]);

  const displayBudgets = localBudgets && localBudgets.length > 0 ? localBudgets : budgets;
  const summaryBudget = localBudget || activeBudget;
  const summaryBudgetValues = localBudget?.values && Object.keys(localBudget.values).length > 0
    ? localBudget.values
    : budgetValues;
  const summaryCards = localReport ? [
    { label: 'Actual', value: localReport.totals.actual, className: '' },
    { label: 'Budget', value: localReport.totals.budget, className: '' },
    {
      label: 'Variance',
      value: localReport.totals.variance || (localReport.totals.actual - localReport.totals.budget),
      className: (localReport.totals.variance || (localReport.totals.actual - localReport.totals.budget)) > 0
        ? 'text-green-600'
        : (localReport.totals.variance || (localReport.totals.actual - localReport.totals.budget)) < 0
          ? 'text-red-600'
          : '',
    },
  ] : [];

  const groupedAccounts = {
    'Revenue': accounts.filter(a => a.type === 'Sales' || a.type === 'Other Income' || a.category === 'Revenue'),
    'Cost of Sales': accounts.filter(a => a.type === 'Direct Cost'),
    'Operating Expenses': accounts.filter(a => a.type === 'Expense' || (a.category === 'Expense' && a.type !== 'Direct Cost')),
    'Assets': accounts.filter(a => ['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset'].includes(a.type)),
    'Liabilities': accounts.filter(a => ['Current Liability', 'Non-current Liability'].includes(a.type)),
    'Equity': accounts.filter(a => a.type === 'Equity')
  };

  const summaryMonths = (() => {
    const months = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    
    while (current <= endMonth) {
      months.push(current.toISOString().split('T')[0].substring(0, 7));
      current.setMonth(current.getMonth() + 1);
    }
    
    for (let i = 0; i < budgetPeriod; i++) {
       months.push(current.toISOString().split('T')[0].substring(0, 7));
       current.setMonth(current.getMonth() + 1);
    }
    
    return Array.from(new Set(months));
  })();

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Future Periods to Show</span>
            <select 
              value={budgetPeriod}
              onChange={async (e) => {
                const newPeriod = Number(e.target.value);
                setBudgetPeriod(newPeriod);
                if (selectedBudgetId) {
                  try {
                    if (appAuthToken && localOrgId) {
                      await fetch(`/api/organizations/${encodeURIComponent(localOrgId)}/budgets/${encodeURIComponent(selectedBudgetId)}`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${appAuthToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ period: newPeriod }),
                      });
                    } else {
                      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
                        period: newPeriod,
                        updatedAt: serverTimestamp()
                      });
                    }
                  } catch (error) {
                    handleFirestoreError(error, OperationType.UPDATE, 'budgets');
                  }
                }
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option value={0}>0 Months</option>
              <option value={3}>3 Months</option>
              <option value={6}>6 Months</option>
              <option value={12}>12 Months</option>
              <option value={24}>24 Months</option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Budget</span>
            <select 
              value={selectedBudgetId || ''}
              onChange={(e) => {
                if (e.target.value === 'new') setIsBudgetModalOpen(true);
                else setSelectedBudgetId(e.target.value);
              }}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer min-w-[120px]"
            >
              <option value="" disabled>Select Budget</option>
              {displayBudgets.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="new">+ Add new budget</option>
            </select>
          </div>

          <button
            onClick={() => setIsBudgetModalOpen(true)}
            className="p-2 bg-[#141414] text-[#E4E3E0] rounded-full hover:scale-110 transition-transform shadow-lg"
            title="Create New Budget"
          >
            <Plus className="w-4 h-4" />
          </button>
          
          {trackingCategories.map(category => (
            <div key={category.id} className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">{category.name}</span>
              <select 
                value={summaryBudget?.trackingSelections?.[category.id] || ''}
                disabled={!summaryBudget}
                onChange={async (e) => {
                  if (!selectedBudgetId) return;
                  try {
                    if (appAuthToken && localOrgId) {
                      const currentSelections = { ...(summaryBudget?.trackingSelections || {}), [category.id]: e.target.value };
                      await fetch(`/api/organizations/${encodeURIComponent(localOrgId)}/budgets/${encodeURIComponent(selectedBudgetId)}`, {
                        method: 'PATCH',
                        headers: { Authorization: `Bearer ${appAuthToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ trackingSelections: currentSelections }),
                      });
                    } else {
                      await updateDoc(doc(db, 'budgets', selectedBudgetId), {
                        [`trackingSelections.${category.id}`]: e.target.value,
                        updatedAt: serverTimestamp()
                      });
                    }
                  } catch (error) {
                    handleFirestoreError(error, OperationType.UPDATE, 'budgets');
                  }
                }}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
              >
                <option value="">All {category.name}</option>
                {category.options?.map((option: string) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          ))}

          <button className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Budget Summary</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            {summaryBudget?.name} • {new Date(startDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} to {new Date(summaryMonths[summaryMonths.length - 1] + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
        </div>

        {summaryCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {summaryCards.map(card => (
              <div key={card.label} className="border border-[#141414] rounded-2xl p-5 bg-white/40">
                <p className="text-[10px] uppercase font-bold tracking-widest opacity-40">{card.label}</p>
                <p className={`mt-2 text-2xl font-mono font-bold ${card.className}`}>
                  {card.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Account</th>
                {summaryMonths.map((month, idx) => (
                  <th key={`summary-header-${month}-${idx}`} className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">
                    {new Date(month + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })}
                  </th>
                ))}
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              {Object.entries(groupedAccounts).map(([category, categoryAccounts]) => {
                const categoryTotalByMonth = summaryMonths.map(month => 
                  categoryAccounts.reduce((sum, acc) => sum + (summaryBudgetValues[`${acc.code}_${month}`] || 0), 0)
                );
                const categoryGrandTotal = categoryTotalByMonth.reduce((a, b) => a + b, 0);

                return (
                  <React.Fragment key={category}>
                    <tr className="bg-[#141414]/5">
                      <td colSpan={summaryMonths.length + 2} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">{category}</td>
                    </tr>
                    {categoryAccounts.map(account => {
                      const rowTotal = summaryMonths.reduce((sum, month) => sum + (summaryBudgetValues[`${account.code}_${month}`] || 0), 0);
                      if (rowTotal === 0) return null;

                      return (
                        <tr key={account.id} className="hover:bg-[#141414]/5 transition-colors">
                          <td className="p-4 text-xs font-bold">{account.name}</td>
                          {summaryMonths.map((month, idx) => (
                            <td key={`${account.id}-${month}-${idx}`} className="p-4 text-right font-mono text-xs">
                              {(summaryBudgetValues[`${account.code}_${month}`] || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                          ))}
                          <td className="p-4 text-right font-mono text-xs font-bold">
                            {rowTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-[#141414]/2 font-bold">
                      <td className="p-4 text-xs italic serif">Total {category}</td>
                      {categoryTotalByMonth.map((total, idx) => (
                        <td key={idx} className="p-4 text-right font-mono text-xs">
                          {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                      ))}
                      <td className="p-4 text-right font-mono text-xs">
                        {categoryGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isBudgetModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsBudgetModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-3xl shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
            <div className="px-8 py-6 border-b-2 border-[#141414] flex justify-between items-center">
              <h2 className="text-2xl font-bold italic serif">New Budget</h2>
              <button onClick={() => setIsBudgetModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">Budget Name</label>
                <input 
                  type="text"
                  value={newBudgetName}
                  onChange={(e) => setNewBudgetName(e.target.value)}
                  placeholder="e.g. 2026 Growth Plan"
                  className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                />
              </div>
              {trackingCategories.map(category => (
                <div key={category.id}>
                  <label className="block text-[10px] font-bold uppercase tracking-widest opacity-70 mb-2">{category.name}</label>
                  <select 
                    value={newBudgetTrackingSelections[category.id] || ''}
                    onChange={(e) => setNewBudgetTrackingSelections(prev => ({
                      ...prev,
                      [category.id]: e.target.value
                    }))}
                    className="w-full px-4 py-3 bg-white/50 border-2 border-[#141414] rounded-xl font-bold focus:outline-none focus:ring-4 focus:ring-[#141414]/5 transition-all"
                  >
                    <option value="">All {category.name}</option>
                    {category.options?.map((option: string) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button 
                onClick={handleCreateBudget}
                disabled={!newBudgetName.trim() || isSavingBudget}
                className="w-full py-4 bg-[#141414] text-[#E4E3E0] rounded-full font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSavingBudget && <Loader2 className="w-4 h-4 animate-spin" />}
                Create Budget
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
